class CollectionInfiniteScroll extends HTMLElement {
  connectedCallback() {
    if (!('IntersectionObserver' in window)) return;
    this.grid = this.closest('#ProductGridContainer')?.querySelector('#product-grid');
    this.status = this.querySelector('[data-scroll-status]');
    this.fallback = this.querySelector('[data-pagination-fallback]');
    if (!this.grid || !this.fallback) return;
    this.stopped = false;
    this.fallback.hidden = true;
    this.style.display = 'block';
    this.style.minHeight = '1px';
    this.onScroll = () => {
      if (this.getBoundingClientRect().top <= window.innerHeight + 400) this.loadNext();
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.cancel = () => {
      window.removeEventListener('scroll', this.onScroll);
      this.stopped = true;
      this.observer?.disconnect();
      this.controller?.abort();
    };
    document.addEventListener('collection:refresh', this.cancel);
    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
    }, { rootMargin: '400px 0px' });
    if (this.dataset.nextUrl) this.observer.observe(this);
  }

  disconnectedCallback() {
    this.cancel?.();
    document.removeEventListener('collection:refresh', this.cancel);
  }

  async loadNext() {
    if (this.loading || this.stopped || !this.dataset.nextUrl) return;
    this.loading = true;
    this.controller = new AbortController();
    this.status.textContent = 'Loading more products…';
    try {
      const url = new URL(this.dataset.nextUrl, window.location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error('Collection request failed');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextGrid = html.querySelector('#product-grid');
      const nextScroll = html.querySelector('collection-infinite-scroll');
      if (!nextGrid?.children.length || !nextScroll) throw new Error('Collection response incomplete');
      if (this.stopped || !this.isConnected) return;
      const cards = [...nextGrid.children];
      cards.forEach((card) => {
        card.classList.add('scroll-trigger--cancel');
        card.querySelectorAll('.scroll-trigger').forEach((el) => el.classList.add('scroll-trigger--cancel'));
      });
      this.grid.append(...cards);
      this.dataset.nextUrl = nextScroll.dataset.nextUrl || '';
      this.fallback.innerHTML = nextScroll.querySelector('[data-pagination-fallback]').innerHTML;
      this.status.textContent = `${cards.length} more products loaded.`;
      this.observer.unobserve(this);
      if (this.dataset.nextUrl) this.observer.observe(this);
      else {
        this.observer.disconnect();
        window.removeEventListener('scroll', this.onScroll);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.status.textContent = 'More products are available on the next page.';
        this.fallback.hidden = false;
        this.observer.disconnect();
        window.removeEventListener('scroll', this.onScroll);
      }
    } finally {
      this.loading = false;
    }
  }
}

if (!customElements.get('collection-infinite-scroll')) {
  customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);
}
